class AnnouncementBar {
	static selectors = {
		slide: '[data-announcement-slide]',
		previous: '[data-announcement-previous]',
		next: '[data-announcement-next]',
	};

	static classes = {
		active: 'is-active',
		enterNext: 'is-entering-next',
		leaveNext: 'is-leaving-next',
		enterPrevious: 'is-entering-previous',
		leavePrevious: 'is-leaving-previous',
	};

	constructor(element) {
		this.element = element;
		this.slides = Array.from(
			element.querySelectorAll(AnnouncementBar.selectors.slide)
		);

		this.previousButton = element.querySelector(
			AnnouncementBar.selectors.previous
		);

		this.nextButton = element.querySelector(
			AnnouncementBar.selectors.next
		);

		this.currentIndex = this.getInitialIndex();
		this.intervalId = null;
		this.isAnimating = false;
		this.touchStartX = 0;

		this.autoplay = element.dataset.autoplay === 'true';
		this.pauseOnHover = element.dataset.pauseOnHover === 'true';
		this.autoplaySpeed =
			Number.parseInt(element.dataset.autoplaySpeed, 10) || 5000;

		this.handlePreviousClick = () => {
			this.goTo(this.currentIndex - 1, 'previous', true);
		};

		this.handleNextClick = () => {
			this.goTo(this.currentIndex + 1, 'next', true);
		};

		this.handleMouseEnter = () => {
			this.stopAutoplay();
		};

		this.handleMouseLeave = () => {
			this.startAutoplay();
		};

		this.handleVisibilityChange = () => {
			document.hidden
				? this.stopAutoplay()
				: this.startAutoplay();
		};

		this.handleTouchStart = (event) => {
			this.touchStartX =
				event.changedTouches[0]?.clientX || 0;
		};

		this.handleTouchEnd = (event) => {
			const touchEndX =
				event.changedTouches[0]?.clientX || 0;

			const difference = this.touchStartX - touchEndX;
			const swipeThreshold = 50;

			if (Math.abs(difference) < swipeThreshold) return;

			if (difference > 0) {
				this.goTo(this.currentIndex + 1, 'next', true);
				return;
			}

			this.goTo(
				this.currentIndex - 1,
				'previous',
				true
			);
		};

		this.handleResize = () => {
			this.updateHeight();
		};

		this.init();
	}

	init() {
		if (!this.slides.length) return;

		this.setInitialState();
		this.updateAccessibility();
		this.updateHeight();
		window.addEventListener('resize', this.handleResize);

		if (this.slides.length < 2) return;

		this.addEventListeners();
		this.startAutoplay();
	}

	getInitialIndex() {
		const activeIndex = this.slides.findIndex((slide) =>
			slide.classList.contains(
				AnnouncementBar.classes.active
			)
		);

		return activeIndex >= 0 ? activeIndex : 0;
	}

	setInitialState() {
		this.slides.forEach((slide, index) => {
			slide.classList.toggle(
				AnnouncementBar.classes.active,
				index === this.currentIndex
			);
		});
	}

	addEventListeners() {
		this.previousButton?.addEventListener(
			'click',
			this.handlePreviousClick
		);

		this.nextButton?.addEventListener(
			'click',
			this.handleNextClick
		);

		this.element.addEventListener(
			'touchstart',
			this.handleTouchStart,
			{ passive: true }
		);

		this.element.addEventListener(
			'touchend',
			this.handleTouchEnd,
			{ passive: true }
		);

		document.addEventListener(
			'visibilitychange',
			this.handleVisibilityChange
		);

		if (!this.pauseOnHover) return;

		this.element.addEventListener(
			'mouseenter',
			this.handleMouseEnter
		);

		this.element.addEventListener(
			'mouseleave',
			this.handleMouseLeave
		);

		this.element.addEventListener(
			'focusin',
			this.handleMouseEnter
		);

		this.element.addEventListener(
			'focusout',
			this.handleMouseLeave
		);
	}

	removeEventListeners() {
		this.previousButton?.removeEventListener(
			'click',
			this.handlePreviousClick
		);

		this.nextButton?.removeEventListener(
			'click',
			this.handleNextClick
		);

		this.element.removeEventListener(
			'touchstart',
			this.handleTouchStart
		);

		this.element.removeEventListener(
			'touchend',
			this.handleTouchEnd
		);

		document.removeEventListener(
			'visibilitychange',
			this.handleVisibilityChange
		);

		if (!this.pauseOnHover) return;

		this.element.removeEventListener(
			'mouseenter',
			this.handleMouseEnter
		);

		this.element.removeEventListener(
			'mouseleave',
			this.handleMouseLeave
		);

		this.element.removeEventListener(
			'focusin',
			this.handleMouseEnter
		);

		this.element.removeEventListener(
			'focusout',
			this.handleMouseLeave
		);
	}

	goTo(index, direction = 'next', restartAutoplay = false) {
		if (this.isAnimating) return;

		const nextIndex =
			((index % this.slides.length) + this.slides.length) %
			this.slides.length;

		if (nextIndex === this.currentIndex) return;

		this.changeSlide(nextIndex, direction);

		if (restartAutoplay) {
			this.restartAutoplay();
		}
	}

	changeSlide(nextIndex, direction) {
		const currentSlide = this.slides[this.currentIndex];
		const nextSlide = this.slides[nextIndex];

		const isPrevious = direction === 'previous';

		const enteringClass = isPrevious
			? AnnouncementBar.classes.enterPrevious
			: AnnouncementBar.classes.enterNext;

		const leavingClass = isPrevious
			? AnnouncementBar.classes.leavePrevious
			: AnnouncementBar.classes.leaveNext;

		this.isAnimating = true;

		currentSlide.classList.remove(
			AnnouncementBar.classes.active
		);

		currentSlide.classList.add(leavingClass);
		nextSlide.classList.add(enteringClass);

		let transitionCompleted = false;
		const completeTransition = () => {
			if (transitionCompleted) return;
			transitionCompleted = true;

			nextSlide.removeEventListener(
				'animationend',
				completeTransition
			);

			currentSlide.classList.remove(leavingClass);
			nextSlide.classList.remove(enteringClass);
			nextSlide.classList.add(
				AnnouncementBar.classes.active
			);

			this.currentIndex = nextIndex;
			this.isAnimating = false;

			this.updateAccessibility();
		};

		nextSlide.addEventListener(
			'animationend',
			completeTransition,
			{ once: true }
		);

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			completeTransition();
		}
	}

	updateAccessibility() {
		this.slides.forEach((slide, index) => {
			const isActive = index === this.currentIndex;
			const link = slide.querySelector('a');

			slide.setAttribute(
				'aria-hidden',
				String(!isActive)
			);

			if (link) {
				link.tabIndex = isActive ? 0 : -1;
			}
		});
	}

	updateHeight() {
		if (!this.element) return;
		const height = this.element.offsetHeight;
		document.documentElement.style.setProperty(
			'--announcement-bar-height',
			`${height}px`
		);
	}


	startAutoplay() {
		if (!this.autoplay) return;
		if (this.slides.length < 2) return;
		if (this.intervalId) return;
		if (document.hidden) return;

		this.intervalId = window.setInterval(() => {
			this.goTo(this.currentIndex + 1);
		}, this.autoplaySpeed);
	}

	stopAutoplay() {
		if (!this.intervalId) return;

		window.clearInterval(this.intervalId);
		this.intervalId = null;
	}

	restartAutoplay() {
		this.stopAutoplay();
		this.startAutoplay();
	}

	destroy() {
		this.stopAutoplay();
		this.removeEventListeners();
		window.removeEventListener('resize', this.handleResize);

		delete this.element.announcementBar;
	}
}

const initAnnouncementBars = (container = document) => {
	container
		.querySelectorAll('[data-announcement-bar]')
		.forEach((element) => {
			if (element.announcementBar) return;

			element.announcementBar =
				new AnnouncementBar(element);
		});
};

initAnnouncementBars();

document.addEventListener('shopify:section:load', (event) => {
	initAnnouncementBars(event.target);
});

document.addEventListener(
	'shopify:section:unload',
	(event) => {
		event.target
			.querySelector('[data-announcement-bar]')
			?.announcementBar?.destroy();
	}
);
